import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryState } from 'nuqs';
import { toast } from 'sonner';
import type { FilterGroup, GroupLogic } from '@shared/flow/filter';
import { normalizeGroupBy, type FlowSort, type FlowView, type FlowViewsResponse, type SavedViewWire } from '@shared/flow/view';
import type { FlowWorkingState } from '@shared/flow/view-state';
import type { FlowListLayoutId } from '@shared/flow/flow-layouts';
import { viewDefinitionsEqual } from '@shared/flow/view-normalize';
import { isKnownFilterField } from '@shared/flow/filter-fields';
import { CONTACT_PRESETS } from '@shared/flow/contact-view';
import { ACCOUNT_PRESETS } from '@shared/flow/account-view';
import { SIGNAL_PRESETS } from '@shared/flow/signal-view';
import { PLAYS_PRESETS } from '@shared/flow/plays-table';
import { useUserControlsContext } from '../../../contexts/UserControlsContext';
import { toastUnlessRefused } from '../../../lib/errors';
import { useFlowViews, useFlowViewMutations, flowViewsQueryKey, type FlowViewEntity } from './useViews';
import { copyViewLink } from './copyViewLink';
import type { FlowViewStateStore } from './useViewState';
import { stripSyntheticColumns } from './syntheticColumns';

/**
 * The saved-views state machine (plan U6) — active view, live working state,
 * dirty diff, and every Open/Save/Save-as-new/Reset/rename/delete operation,
 * for one entity's table.
 *
 * It replaces the per-mount `switchView` local-state reset that wiped a
 * rep's filters on every tab switch (Rob Cook, 2026-08-11). Both mounts
 * (`ContactFlowTable`, `AccountFlowTable`) drive their FlowTable from this
 * one hook, so the two tables cannot drift.
 *
 * ── The dirty rule (doc's dirty-state table, verbatim) ──────────────────
 * Dirty = a normalized compare of `{filter, groupLogic, sort, groupBy,
 * columns}` against the ACTIVE VIEW's definition, through the ONE canonical
 * normalizer (`@shared/flow/view-normalize`) the server's save path also
 * uses. Columns compare as one ordered array, so add / remove / REORDER are
 * the same comparison (R4). The search box is not in `FlowWorkingState` at
 * all, so typing in it can never dirty a view. A rename is not part of the
 * definition either — renaming never trips the Modified banner.
 *
 * ── Baseline vs. the raw view ───────────────────────────────────────────
 * The dirty baseline is the view AS OPENED — i.e. after `adaptOnOpen`, the
 * mount's hook for state that belongs to the MOUNT rather than to the view
 * (role-default `{me}` ownership rows on presets; the account drill-down
 * dropping the Account column). Comparing against the raw view instead
 * would show "Modified" to a ClayDR who has done nothing but land on the
 * page.
 *
 * ── groupLogic persistence (D2) ──────────────────────────────────────────
 * `flow_views.group_logic` persists the outer AND/OR combinator, so a saved
 * view now round-trips whatever the rep set. Presets carry no `groupLogic`
 * key (they have no stored combinator), so `workingFromView` still seeds
 * `'AND'` for them via the same default the normalizer uses.
 *
 * ── `?view=` is a nuqs param, NOT react-router's (U1) ────────────────────
 * This hook reads and writes `?view=` through `useQueryState`, never through
 * react-router's `useSearchParams`. Command Center owns `?tab=` via nuqs,
 * whose default (shallow) write is a bare `history.replaceState` that
 * react-router never observes — so a react-router `setSearchParams(prev =>
 * …)` here rebases on a snapshot that is missing `tab=contact`, drops it,
 * and (because nuqs patches the History API to follow third-party
 * navigations) makes nuqs adopt the tab-less URL: the rep clicks "Contacts"
 * and bounces straight back to Accounts. Rebasing on `window.location`
 * instead does NOT fix it — the router write still trips nuqs's queue reset.
 * One shared writer queue is the fix. Regression test:
 * `web/app-home/src/__tests__/command-center-tab-survival.test.tsx`.
 */

export type ViewKind = 'preset' | 'saved';

/**
 * Asks the CALLER for a view name (U2a). Every naming UI lives in
 * `ViewNameDialog`; this hook owns the write and nothing else, so the paths
 * that need a name mid-operation (`copyLink` on a modified view) take one of
 * these instead of reaching for `window.prompt`. It is handed the suggested
 * default to pre-fill and resolves `null` when the rep backs out.
 *
 * Deliberately NOT named `RequestViewName` — that name belongs to
 * `ViewNameDialog`'s richer request object (`{title, initialValue,
 * confirmLabel}`). Two same-named exported types with different shapes in one
 * folder is exactly the drift the codebase's schema/type rules exist to stop;
 * callers adapt the dialog's API to this one-argument shape at the call site.
 */
export type SuggestViewName = (suggestedName: string) => Promise<string | null>;

/**
 * Every entity's code-defined presets, keyed by entity — the single source
 * `FOREIGN_PRESET_IDS`/`OTHER_ENTITIES` below both derive from, so a fourth
 * entity (`play`) only ever needed one new line here (and TS's `Record<FlowViewEntity,
 * ...>` on all three maps forces that line to exist — `bun run check` is
 * the proof the bead's acceptance criteria calls for).
 */
export const PRESETS_BY_ENTITY: Record<FlowViewEntity, FlowView[]> = {
  contact: CONTACT_PRESETS,
  account: ACCOUNT_PRESETS,
  signal: SIGNAL_PRESETS,
  play: PLAYS_PRESETS,
};

/**
 * Every OTHER entity, for a given entity — Command Center keeps all tables on
 * one URL (`?tab=`) and one `QueryClient`, so a `?view=` left behind by
 * another table's tab is still in the query string (or another table's
 * cached saved-views list is still in the query cache) when THIS table
 * mounts. `belongsToOtherEntity` walks this list to recognise such a param
 * silently instead of toasting "that view no longer exists" on every tab
 * switch.
 */
export const OTHER_ENTITIES: Record<FlowViewEntity, FlowViewEntity[]> = {
  contact: ['account', 'signal', 'play'],
  account: ['contact', 'signal', 'play'],
  signal: ['contact', 'account', 'play'],
  play: ['contact', 'account', 'signal'],
};

/** Preset ids belonging to any OTHER entity — see `OTHER_ENTITIES`'s doc above. */
const FOREIGN_PRESET_IDS: Record<FlowViewEntity, Set<string>> = {
  contact: new Set(OTHER_ENTITIES.contact.flatMap(other => PRESETS_BY_ENTITY[other].map(p => p.id))),
  account: new Set(OTHER_ENTITIES.account.flatMap(other => PRESETS_BY_ENTITY[other].map(p => p.id))),
  signal: new Set(OTHER_ENTITIES.signal.flatMap(other => PRESETS_BY_ENTITY[other].map(p => p.id))),
  play: new Set(OTHER_ENTITIES.play.flatMap(other => PRESETS_BY_ENTITY[other].map(p => p.id))),
};

export interface UseViewStateMachineOptions {
  entity: FlowViewEntity;
  /** Code-defined presets for this mount, already mount-adapted by the caller. */
  presets: FlowView[];
  defaultPresetId: string;
  /**
   * Gate for the FIRST resolution. The mount holds this false until whatever
   * `adaptOnOpen` depends on has settled (identity, for role defaults), so
   * the initial working state is never seeded twice — no
   * unfiltered-then-filtered flash and no phantom Modified banner.
   */
  ready?: boolean;
  /**
   * Mount-owned adaptation applied to a view as it becomes active. Called
   * for presets (which get the rep's role default) and for saved views
   * (which own their own predicate and get only structural mount
   * adaptation). The RESULT becomes both the working state and the dirty
   * baseline.
   */
  adaptOnOpen?: (view: FlowView, kind: ViewKind) => FlowView;
  /**
   * The per-(user, entity) working-state store (U9 / R8) — `useFlowViewState`.
   * Supplies the restore payload consumed by the first resolution below and
   * the default-view pointer the switcher's "Set as default" writes. Omitted
   * on mounts that must not own the user's state (the account-scoped contact
   * roster), which then behave exactly as they did before U9.
   */
  viewState?: FlowViewStateStore | null;
  /**
   * Resolve WITHOUT the stored working state (U2 / R1(b), 2026-08-27).
   *
   * Rung 2 of the first resolution below (stored `activeViewId` + its
   * `workingState`) is skipped entirely when this is set: resolution falls
   * straight through to rung 3 (the `defaultViewId` POINTER, which still
   * applies) and then rung 4 (the code preset). The mount therefore always
   * lands on a view's OWN clean definition.
   *
   * Set by the account-scoped `ContactFlowTable` (drill-down / main-Terra
   * Contacts tab). Every drill-down shares ONE `contact_account` state row
   * (deliberately — a per-account row would multiply unboundedly, see
   * `useViewState.ts:46-59`), so restoring that row's working state means
   * account A's filters reappear on account B. Reps (Annie Hale) read that
   * as filters "carrying over" into every drill-down; Spencer's decision
   * (2026-08-27) is to keep the shared row but stop restoring its working
   * half. The WRITE half stays on: the row keeps recording state, and its
   * `defaultViewId` pointer is still read at rung 3.
   *
   * Off by default — the global Command Center mounts keep the full U9
   * restore, banner and all.
   */
  skipWorkingRestore?: boolean;
  /**
   * Switch views WITHOUT carrying the outgoing view's dirty filter (U2 /
   * R1(c), 2026-08-27) — `openView` forces `carryFilterFrom` to null, so the
   * target always gets its own definition.
   *
   * A deliberate PARTIAL reversal of the 2026-08-12 carry-forward product ask
   * (`applyView`'s `carryFilterFrom` doc below), scoped to account mounts
   * only: inside a drill-down the carry is what makes a preset switch feel
   * like the previous view's filters "stuck". The global Command Center mount
   * keeps carry-forward exactly as shipped.
   *
   * Off by default.
   */
  disableFilterCarry?: boolean;
  /**
   * The nuqs key this machine reads/writes (plan 2026-09-25-002 U3) — default
   * `'view'`. A page that mounts SEVERAL machines of one entity gives each its
   * own key so they never observe each other's URL param: the Plays tab's
   * three contact mounts derive `` `view_${stateKey}` `` (`useContactMount`'s
   * `viewParam` fact) for their play-section surfaces, while every other
   * mount (People, Plays, Accounts, Signals) keeps the bare default.
   * Everything else about the machine (resolution precedence, dirty diff,
   * `belongsToOtherEntity`) is unaffected — `belongsToOtherEntity` reasons
   * about ENTITIES, not URL params, so namespacing a param does not change it.
   */
  viewParam?: string;
  /**
   * Retired preset id -> the preset that replaced it. A `?view=` value found
   * here resolves to the replacement SILENTLY (no "no longer exists" toast) and
   * the param is rewritten to the new id, so an old shared link keeps working.
   * Contact mounts pass `CONTACT_PRESET_ID_ALIASES`.
   */
  presetIdAliases?: Readonly<Record<string, string>>;
}

export interface FlowViewMachine {
  entity: FlowViewEntity;
  /** False until the first view has been resolved — mounts gate their fetch on it. */
  isReady: boolean;
  presets: FlowView[];
  /** Saved views created by the acting user (switcher's "Your views"). */
  myViews: SavedViewWire[];
  /** Saved views created by anyone else (switcher's "Team views"). */
  teamViews: SavedViewWire[];
  isLoadingViews: boolean;

  activeViewId: string;
  activeView: FlowView;
  isPreset: boolean;
  /** The acting user may Save in place — i.e. this is their own saved view. */
  canSave: boolean;
  /** Creator display name when the active view belongs to somebody else. */
  creatorName: string | null;
  /**
   * The switcher trigger's third segment (U2b, decision c) — "Global" for a
   * preset, "You" for the acting rep's own saved view, otherwise the
   * teammate's `ownerDisplayLabel`. Row-level `byLabel`s in `ViewSwitcher`
   * use the same vocabulary so the trigger and the list never disagree.
   */
  ownershipLabel: string;

  working: FlowWorkingState;
  /** The view as opened — what Reset restores and what dirty compares against. */
  baseline: FlowWorkingState;
  isDirty: boolean;
  isSaving: boolean;

  setFilters: (groups: FilterGroup[]) => void;
  setGroupLogic: (logic: GroupLogic) => void;
  setSort: (sort: FlowSort) => void;
  /** Ordered grouping levels (PR1 U4/D7/D8, R5) — `[]` clears grouping. */
  setGroupBy: (levels: string[]) => void;
  setColumns: (columns: string[]) => void;
  /**
   * Rep-dragged column widths in px, keyed by column key (U10 / R12) — the
   * whole map, replaced wholesale (FlowTable's resize handle owns the merge,
   * since it is the only producer). NOT part of the view definition: it never
   * dirties, it is never written to `flow_views`, and it survives view
   * switches and Reset. See `FlowWorkingStateSchema.columnWidths`.
   */
  setColumnWidths: (columnWidths: Record<string, number>) => void;

  /**
   * Switch the active view. Always applies the target immediately —
   * whatever was on screen is overwritten as WORKING state, but never lost:
   * the outgoing view's committed filter carries forward onto the newly
   * active **preset** (view-switch filter carry, product ask 2026-08-12 —
   * see `applyView`'s `carryFilterFrom` doc), and `useFlowViewState`'s
   * `useFlowViewStatePersist` (F8) already persists a dirty edit to the
   * server on a ~1s debounce and flushes synchronously on unmount, so a view
   * switch can never destroy an unsaved edit the way it once did.
   *
   * Opening a **saved** view is different: nothing carries onto it. It
   * applies that view's own definition exactly, identical to the `?view=`
   * deep link — a saved view owns its complete predicate, so a teammate's
   * shared view shows exactly what they shared, whichever path you reached
   * it by. Fixed 2026-08-13 after carry ran regardless of `ViewKind`; see
   * docs/flow/shared-view-vs-role-defaults-bug.md.
   *
   * (This used to ask a `guard` before a dirty switch — V1, QA-2, 2026-08-12
   * — offering Save / Save-as-new / Discard / Keep-editing. Removed
   * 2026-08-12: once filter carry became unconditional on preset targets
   * (applying on every outcome, including 'discard'), the dialog's "Discard"
   * button no longer discarded anything, and F8 already made the underlying
   * state-loss risk this dialog existed to prevent impossible independently.
   * See `useViewLeaveGuardDialog.tsx` in git history for the removed UI.)
   *
   * Resolves `true` iff the target view was actually applied (`false` only
   * on an unresolvable id, which degrades to the default preset) — a caller
   * that must decide whether to dismiss its OWN chrome (`BrowseAllModal`'s
   * drawer) reads this rather than guessing from a stale render-time
   * closure.
   *
   * `opts.carry === false` is a PER-CALL opt-out of the view-switch filter
   * carry (default `true`): the target preset lands on its own definition and
   * reads clean. The favorites pass `{ carry: false }` so a chip click
   * never reads Modified; the pane and switcher keep the default. Unlike the
   * mount-wide `disableFilterCarry`, it affects only that one call.
   */
  openView: (id: string, opts?: { carry?: boolean }) => Promise<boolean>;
  /**
   * Land on a view that was JUST created, from the create response itself.
   *
   * The id round-trip (`openView(created.id)`) cannot work for a fresh row:
   * `openView` resolves against `savedById`, which is derived from the views
   * QUERY, and that query has not refetched yet at the moment a caller holds
   * the create response — so the brand-new id is absent and `openView`
   * degrades to its "that view no longer exists" branch. The response IS the
   * row; apply it. Callers that create a view MUST use this rather than
   * `openView` (F6).
   */
  applyCreated: (created: SavedViewWire) => void;
  reset: () => void;
  /**
   * Save the working definition onto the active (own) saved view. `flowLayout`
   * pins a list layout to the view (U11, KTD-6) — omitted, the PUT carries no
   * pin key and the stored pin is untouched. Resolves the updated row, or
   * `null` when the view is a preset/teammate's (read-only guard) or the write
   * failed (already toasted).
   */
  save: (options?: { flowLayout?: FlowListLayoutId }) => Promise<SavedViewWire | null>;
  /**
   * Create a new saved view from the CURRENT working state under `name`, and
   * land on it. This is BOTH "Save as new view" and "+ New view" (U2a,
   * decision b): a new view is named up front and written immediately, seeded
   * from whatever is on screen — there is no draft state in between.
   * `options.flowLayout` pins a list layout to the new view (U11, KTD-6);
   * omitted, the new view inherits the active view's own pin, if any.
   */
  saveAsNew: (name: string, options?: { flowLayout?: FlowListLayoutId }) => Promise<SavedViewWire | null>;
  /**
   * Set by a mount whose working state is not the whole story (Signals' URL-only "All else" exclusion): `save` and
   * `saveAsNew` refuse with this tip, and every create affordance (Save, Save as new, New view, the pane's "+")
   * disables itself with it. `saveCopyOf` copies a stored view, so it stays allowed.
   */
  saveBlockedReason?: string | null;
  /**
   * Create a new saved view from ANOTHER view's own saved definition (not the working state)
   * under `name`, and land on it — the pane row's "Save as new view…" (BrowseAllModal's
   * same-entity path, folded into the machine). Presets and teammates' views included.
   */
  saveCopyOf: (view: FlowView, name: string) => Promise<SavedViewWire | null>;
  renameView: (view: SavedViewWire, name: string) => Promise<void>;
  /**
   * Deletes unconditionally — the machine never confirms (thermo-nuclear
   * review S2). Every caller owns its OWN confirm before calling this: V1's
   * `ViewSwitcher` uses `window.confirm`; V2's `DeleteViewConfirm` is its own
   * Glaze `AlertDialog`. Two confirm UIs is a caller-side decision now, not a
   * flag this machine has to branch on.
   */
  deleteView: (view: SavedViewWire) => Promise<void>;
  /**
   * Copy a shareable `?view=` link. Copying the ACTIVE view while it has
   * unsaved edits saves it as a new view first (doc R1 — a link always points
   * at a saved view), which is why this takes a name requester.
   */
  copyLink: (id: string, requestName?: SuggestViewName) => Promise<void>;
  /** The rep's landing view for this entity (U9), or null when unset/unavailable. */
  defaultViewId: string | null;
  /** "Set as default" — null when this mount has no working-state store. */
  setDefaultView: ((id: string) => void) | null;
  /**
   * F11 (sanctioned caching exception, see `useViews.ts`'s module doc) —
   * forces a refetch of the saved-views list. `flow_views` rows are
   * team-visible and this client has no invalidation channel for another
   * user's mutation, so `ViewSwitcher` and `BrowseAllModal` call this the
   * moment either one OPENS, rather than trusting the indefinite cache.
   */
  refetchViews: () => void;
}

/**
 * Ownership label vocabulary (U2b, decision c) — "Global" for a preset
 * (code-defined, deploy-owned), "You" for the acting rep's own saved view,
 * and FIRST NAME + LAST INITIAL for a teammate's ("Spencer Chemtob" →
 * "Spencer C") rather than a bare first name, which collides the moment two
 * teammates share one. Exported so `ViewSwitcher`'s per-row `byLabel` reads
 * off the SAME function as the trigger label instead of drifting from it.
 */
export function ownerDisplayLabel(creatorName: string | null | undefined): string {
  const parts = (creatorName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'Teammate';
  const first = parts[0];
  if (parts.length === 1) return first;
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
  return `${first} ${lastInitial}`;
}

/**
 * View-switch filter carry (product ask, 2026-08-12 — docs/flow/
 * custom-views-notes.md "carry filters across view switches"): drop any
 * carried CONDITION whose field the target ENTITY's filter catalog doesn't
 * recognize (`isKnownFilterField`, which also honors the write-time
 * grandfather list, so a legacy field a pre-existing saved view still
 * carries is never wrongly dropped), then drop any GROUP that pruning left
 * with zero conditions — mirroring `view-normalize.ts`'s own "a conditionless
 * group constrains nothing" rule, so a carry can never leave a phantom empty
 * AND/OR shell sitting in the popover.
 *
 * Catalogs are keyed by ENTITY, not by individual view — every view within
 * one `useViewStateMachine` mount (one entity) shares the same catalog — so
 * in practice this only ever fires for a genuinely stale/legacy field. It
 * exists purely so a carry can never crash the target's filter popover or
 * 400 a later save, per the "field absent from the target catalog" edge
 * case in the carry-forward spec.
 */
function pruneFilterToCatalog(filter: FilterGroup[], entity: FlowViewEntity): FilterGroup[] {
  return filter
    .map(group => ({ ...group, conditions: group.conditions.filter(c => isKnownFilterField(entity, c.field)) }))
    .filter(group => group.conditions.length > 0);
}

function workingFromView(view: FlowView): FlowWorkingState {
  return {
    filter: view.filter,
    // Presets carry no stored `groupLogic` (no combinator to persist); a
    // saved view now always carries an explicit one (D2). Either way the
    // normalizer's own default is AND, so falling back to it here can never
    // read as dirty.
    groupLogic: view.groupLogic ?? 'AND',
    sort: view.sort,
    groupBy: view.groupBy,
    columns: view.columns,
  };
}

function savedToFlowView(wire: SavedViewWire): FlowView {
  return {
    id: wire.id,
    createdBy: wire.createdBy,
    entity: wire.entity,
    name: wire.name,
    filter: wire.filter,
    groupLogic: wire.groupLogic,
    sort: wire.sort,
    // Array-shaped and REQUIRED on `SavedViewWire`'s TYPE now (PR1 U4/D7/D8)
    // — but the client only validates the `GET /api/flow/views` response
    // OBSERVATIONALLY (`validateResponse` logs and returns the raw payload
    // on a schema mismatch rather than dropping it — see its own doc
    // comment), so a legacy/malformed row genuinely CAN reach here without
    // `groupBy` at runtime despite what the type promises. `normalizeGroupBy`
    // is the canonical collapse for exactly that gap (view.ts) — every OTHER
    // shape it tolerates (undefined/null/string) is a real shape this same
    // wire has carried historically. Without this, a saved view missing
    // `groupBy` crashes the FIRST consumer downstream that calls an array
    // method on `working.groupBy` (regression: U2's `effectiveGroupBy`,
    // `ContactFlowTable.tsx`) instead of degrading to "no grouping".
    groupBy: normalizeGroupBy(wire.groupBy),
    columns: wire.columns,
    isDefault: wire.isDefault,
    // The view's layout pin (U11, KTD-6) — not part of the definition, so it
    // never dirties. Omitted when absent so `FlowViewSchema.strict()` and
    // equality checks never see a `flowLayout: undefined` key.
    ...(wire.flowLayout ? { flowLayout: wire.flowLayout } : {}),
  };
}

/**
 * Working state → the definition the create/update bodies carry.
 *
 * `stripSyntheticColumns` drops client-only display columns (e.g.
 * ContactFlowTable's "Terra Actions" pill strip, `CROSS_APP_STATE_KEY`) that
 * a mount injects into `working`/`baseline` for dirty-diff and ordering
 * purposes but that are not real fields — the server's write-time catalogs
 * 400 on them (see syntheticColumns.ts for the full regression writeup).
 * Only the OUTGOING wire payload is stripped; `working`/`baseline`
 * themselves are untouched, so the dirty diff (`viewDefinitionsEqual`,
 * computed on the raw working state, not through this function) never sees
 * a mismatch from the strip.
 */
function definitionOf(working: FlowWorkingState) {
  return {
    filter: working.filter,
    groupLogic: working.groupLogic,
    sort: working.sort,
    // Array-shaped end to end now (PR1 U4/D7/D8) — `working.groupBy` is
    // ALWAYS an array (`[]` means "no grouping"), so there is no
    // undefined/null case left to coalesce; the server's `GroupBySchema`
    // preprocess (`normalizeGroupBy`) accepts `[]` the same way it used to
    // accept `null`.
    groupBy: working.groupBy,
    columns: stripSyntheticColumns(working.columns),
  };
}

export function useViewStateMachine(options: UseViewStateMachineOptions): FlowViewMachine {
  const {
    entity, presets, defaultPresetId, ready = true, adaptOnOpen, viewState = null,
    skipWorkingRestore = false, disableFilterCarry = false, viewParam: urlParamKey = 'view',
    presetIdAliases,
  } = options;

  // nuqs, not react-router — see the "?view= is a nuqs param" note above.
  const [viewParam, setViewQueryParam] = useQueryState(urlParamKey);
  const { userId } = useUserControlsContext();
  const myUserId = userId != null ? String(userId) : null;

  const queryClient = useQueryClient();
  const viewsQuery = useFlowViews(entity);
  const views = useMemo(() => viewsQuery.data ?? [], [viewsQuery.data]);
  const viewsSettled = viewsQuery.isSuccess || viewsQuery.isError;
  // F11 — see `refetchViews` on `FlowViewMachine` and the module doc in
  // `useViews.ts` for why this query gets an explicit on-open refetch.
  const refetchViews = useCallback(() => { void viewsQuery.refetch(); }, [viewsQuery]);
  const { createView, updateView, deleteView: deleteViewRow, recordViewOpen, isMutating } = useFlowViewMutations(entity);

  const presetById = useMemo(() => new Map(presets.map(p => [p.id, p])), [presets]);
  const savedById = useMemo(() => new Map(views.map(v => [v.id, v])), [views]);
  const defaultPreset = presetById.get(defaultPresetId) ?? presets[0];

  const [activeViewId, setActiveViewId] = useState(defaultPreset.id);
  /**
   * The last view APPLIED, kept so the derived identity below can resolve a
   * view the saved-views query does not carry yet. A create response is
   * exactly that case: the row exists on the server, the machine is standing
   * on it, but the list only learns about it one refetch later — and without
   * this the switcher would show the DEFAULT PRESET's name (and disable Save)
   * for that window, or forever if the refetch fails.
   */
  const [appliedView, setAppliedView] = useState<FlowView | null>(null);
  const [working, setWorking] = useState<FlowWorkingState>(() => workingFromView(defaultPreset));
  const [baseline, setBaseline] = useState<FlowWorkingState>(() => workingFromView(defaultPreset));
  const [isReady, setIsReady] = useState(false);

  // `adaptOnOpen` closes over identity, which changes; keeping it in a ref
  // means `applyView` stays stable and the resolution effect doesn't re-run
  // every time the callback's identity changes.
  const adaptRef = useRef(adaptOnOpen);
  adaptRef.current = adaptOnOpen;

  const setViewParam = useCallback((id: string | null) => {
    // `null` removes the param; nuqs defaults to a replace-history write, so
    // this keeps the old `{ replace: true }` semantics. The returned promise
    // resolves when the batched URL write flushes — nothing here awaits it.
    void setViewQueryParam(id);
  }, [setViewQueryParam]);

  const applyView = useCallback((
    view: FlowView,
    kind: ViewKind,
    opts?: {
      writeParam?: boolean;
      ping?: boolean;
      workingOverride?: FlowWorkingState | null;
      /**
       * View-switch filter carry (product ask, 2026-08-12) — the OUTGOING
       * view's committed filter/groupLogic (its `working.filter`/
       * `working.groupLogic` at the moment of the switch, never an unapplied
       * `FilterPopover` draft), carried onto this newly active view's live
       * WORKING state as the new unsaved edit. Only `openView`'s own switch
       * path supplies this: the first-resolution effect (nothing to carry on
       * a fresh mount — edge case a), `save`, `applyCreated`, and
       * `deleteView`'s "standing on the deleted view" fallback all still
       * call `applyView` WITHOUT it, unchanged.
       *
       * A `kind === 'saved'` TARGET ignores carry outright, even if supplied
       * (see the `kind !== 'saved'` guard below) — a saved view owns its
       * complete predicate, so its filter is never replaced by anything.
       * Fixed 2026-08-13 after carry ran regardless of `ViewKind`, letting a
       * teammate's shared view get silently overwritten by the viewer's own
       * filter when opened through the switcher rather than a fresh
       * `?view=` link. See docs/flow/shared-view-vs-role-defaults-bug.md.
       *
       * Pruned against the catalog first (`pruneFilterToCatalog`) so a
       * carried condition on a field the target's entity doesn't recognize
       * is dropped rather than crashing the popover or 400ing a later save
       * (edge case b).
       */
      carryFilterFrom?: { filter: FilterGroup[]; groupLogic: GroupLogic } | null;
    },
  ) => {
    // The BASELINE always comes from the view's OWN stored definition,
    // adapted exactly as it always was (role defaults included, on a
    // preset) — carry-forward never touches it. That is what makes a
    // carried filter register as an unsaved edit via the ordinary dirty
    // diff, rather than silently becoming the new "as saved" shape.
    const baselineAdapted = adaptRef.current ? adaptRef.current(view, kind) : view;
    const baselineNext = workingFromView(baselineAdapted);

    let workingNext = baselineNext;
    // Belt-and-braces `kind` guard: a `kind === 'saved'` target must NEVER
    // have its filter replaced by anything (docs/flow/shared-view-vs-
    // role-defaults-bug.md) — a saved view owns its complete predicate.
    // `openView` (the only caller that ever supplies `carryFilterFrom`) has
    // already been fixed to not pass it on the saved branch, but this
    // condition makes the invariant unbypassable by any future caller
    // instead of relying on every call site remembering.
    if (opts?.carryFilterFrom && kind !== 'saved') {
      // Splice the carried filter onto a SHADOW copy of the target view
      // before running the same `adaptOnOpen` — so `withRoleDefaults`'
      // existing "skip a chip whose id is already present" rule merges the
      // role defaults onto the CARRIED filter for free (edge case c): a
      // chip already in the carried filter (outgoing view had it too) stays
      // as one copy; a chip the carried filter lacks (outgoing was a saved
      // view — those never carry role defaults) still gets appended here,
      // exactly as it would on a bare preset open.
      const carriedFilter = pruneFilterToCatalog(opts.carryFilterFrom.filter, entity);
      const viewForWorking: FlowView = { ...view, filter: carriedFilter, groupLogic: opts.carryFilterFrom.groupLogic };
      const workingAdapted = adaptRef.current ? adaptRef.current(viewForWorking, kind) : viewForWorking;
      workingNext = workingFromView(workingAdapted);
    }

    setActiveViewId(baselineAdapted.id);
    setAppliedView(baselineAdapted);
    // The BASELINE is always the view as opened; only the live working state
    // can be overridden (U9 restore, or now, view-switch filter carry). That
    // split is what makes a restored/carried unsaved edit re-show the
    // Modified banner while a restored state identical to the view stays
    // clean — the dirty diff does the deciding, not a stored "was dirty"
    // flag. `workingOverride` (U9 restore) wins over a carry if a caller
    // ever supplied both — in practice the two are mutually exclusive call
    // sites (initial resolution vs. `openView`'s switch), so this ordering
    // is a safety default, not a live conflict today.
    //
    // COLUMN WIDTHS (U10, Deferred-to-Implementation decision): PRESERVED
    // across every `applyView`, rather than reset to the target's (always
    // absent) widths. A view definition carries no widths at all — presets
    // and saved views alike — so resetting here would mean a rep's drag is
    // discarded by the very next preset click, which is not what "switch
    // views" means to them. A restored working state (U9 rung 2) DOES carry
    // its own widths, and those win: `next.columnWidths ?? prev.columnWidths`
    // makes the stored map authoritative on the one path that has one.
    setWorking(prev => {
      const next = opts?.workingOverride ?? workingNext;
      const columnWidths = next.columnWidths ?? prev.columnWidths;
      // `opts.workingOverride` (U9 rung 2) is `restored?.workingState` —
      // straight off `GET /api/flow/view-state/:entity`, which the client
      // only validates OBSERVATIONALLY (`validateResponse` warns and hands
      // back the raw payload on a schema mismatch rather than dropping it).
      // `FlowWorkingState.groupBy` types as a required array, but a stale/
      // legacy stored row can still reach here without it — re-normalizing
      // here is what makes that promise actually hold for every downstream
      // reader of `working.groupBy` (`ContactFlowTable`/`AccountFlowTable`/
      // `SignalsFlowTable`), instead of pushing an undefined-groupBy check
      // onto each of them individually.
      return { ...next, columnWidths, groupBy: normalizeGroupBy(next.groupBy) };
    });
    setBaseline(baselineNext);
    if (opts?.ping !== false) recordViewOpen(baselineAdapted.id);
    if (opts?.writeParam) setViewParam(baselineAdapted.id);
  }, [recordViewOpen, setViewParam, entity]);

  // ── First resolution ─────────────────────────────────────────────────────
  // Precedence (plan U9, updated by plan 2026-09-25-003 U1):
  //   1. `?<viewParam>=<id>` — an explicit link always decides WHICH view. If
  //      the stored row is standing on that SAME id, its working state comes
  //      back too (rung 2 folded in): a param this page wrote is always
  //      followed by a persist of the same `activeViewId`, so the stored
  //      working half is newer than the view's definition by construction —
  //      this is what lets a section accordion collapse/reopen, a play
  //      switch, a Command Center tab flip, or a reload with the param still
  //      in the URL all restore the rep's edits (Codex r3 #1). A param
  //      naming a DIFFERENT view than the stored row lands on that view's
  //      clean definition, exactly as before.
  //   2. the stored `activeViewId` + `workingState` (server row, U9) — the
  //      rep's unsaved modifications come back, banner and all.
  //   3. the stored `defaultViewId` — their chosen landing view.
  //   4. the code preset flagged default.
  //
  // `skipWorkingRestore` mounts never restore working state, param or not.
  // Rung 2 is skipped entirely on a mount that passes `skipWorkingRestore`
  // (U2 / R1(b), 2026-08-27 — the account-scoped contact roster); see that
  // option's doc and the rung-2 comment below for why.
  //
  // Runs exactly ONCE per mount. `resolvedRef` is deliberately NOT reset in a
  // mount effect: "resolve once" is the wanted semantics including across
  // StrictMode's dev double-invoke (same reasoning as the seed-once refs in
  // ContactFlowTable/AccountFlowTable — see
  // docs/v4-system/strictmode-resilient-effects.md for when a lifetime ref
  // DOES need resetting). The corollary: a mount whose SCOPE can change under
  // it (the account-scoped contact roster, whose `accountId` the main-Terra
  // Contacts tab swaps in place) must remount rather than expect this effect
  // to re-run — `ContactFlowTable` keys itself on `accountId` for exactly that
  // reason, and any future scope-changing mount owes the same key.
  // Mounts additionally hold `ready` false until BOTH
  // identity and the view-state GET have settled, so this never resolves
  // against a half-loaded store.
  const resolvedRef = useRef(false);
  const restored = viewState?.restored ?? null;

  /**
   * Does this `?view=` value demonstrably belong to the OTHER table? (F14)
   *
   * A saved view's id is a bare `flow_views.id` — it carries no entity — so a
   * Command Center tab flip hands the newly mounted machine an id it cannot
   * resolve, which is indistinguishable from a deleted one by inspection.
   * The other entity's views LIST answers it: `flow_views.id` is unique across
   * the whole table, so seeing the id there is proof of ownership, and that
   * list is in the query cache precisely when it matters — the tab we just
   * came from fetched it (cached indefinitely, keyed per entity). No cache
   * entry means we genuinely cannot tell, and the honest read of an
   * unresolvable id is then still "dead link", toast included.
   */
  const belongsToOtherEntity = useCallback((id: string): boolean => {
    if (FOREIGN_PRESET_IDS[entity].has(id)) return true;
    // F12 — the cache entry at this key is the RAW `GET /api/flow/views`
    // response (`{ views, presetUsage }`) now that `presetUsage` rides
    // alongside `views`; `useFlowViews`'s `.views` slice only exists in that
    // hook's `select`, not in what's actually stored under this query key.
    // Checks EVERY other entity's cache, not just one — with three entities
    // sharing one `?view=` param, the id could belong to either of the other
    // two, and a cache miss (that entity's list hasn't loaded yet) is simply
    // skipped rather than treated as proof of ownership.
    return OTHER_ENTITIES[entity].some(other => {
      const otherData = queryClient.getQueryData<FlowViewsResponse>(flowViewsQueryKey(other));
      return otherData?.views.some(v => v.id === id) ?? false;
    });
  }, [entity, queryClient]);

  useEffect(() => {
    if (resolvedRef.current || !ready) return;

    const rawWanted = viewParam !== null && viewParam.length > 0 ? viewParam : null;
    const aliasedWanted = rawWanted !== null && presetIdAliases !== undefined && Object.prototype.hasOwnProperty.call(presetIdAliases, rawWanted)
      ? presetIdAliases[rawWanted]
      : null;
    const wanted = aliasedWanted ?? rawWanted;
    const isPresetParam = wanted !== null && presetById.has(wanted);
    // Our own preset slug always wins over the foreign check (ids never
    // collide, but resolving locally costs nothing and cannot be wrong).
    const isForeign = wanted !== null && !isPresetParam && belongsToOtherEntity(wanted);
    // An explicit param takes the URL branch; anything else falls through to
    // the stored pointers (a foreign param belongs to the other table).
    const useParam = wanted !== null && !isForeign;
    // U2 / R1(b) (2026-08-27): `skipWorkingRestore` nulls the stored ACTIVE
    // pointer here, at its single source, which is what takes rung 2 out of
    // play below — id and working state together, and without the list wait
    // that resolving a saved stored id would otherwise impose. The stored
    // DEFAULT pointer (rung 3) is deliberately untouched.
    const storedActiveId = useParam || skipWorkingRestore ? null : (restored?.activeViewId ?? null);
    const storedDefaultId = useParam ? null : (restored?.defaultViewId ?? null);

    // Only ids that aren't preset slugs need the saved-view list to resolve.
    const needsList = (id: string | null) => id !== null && !presetById.has(id);
    const mustWaitForList = useParam
      ? !isPresetParam
      : (needsList(storedActiveId) || needsList(storedDefaultId));
    if (mustWaitForList && !viewsSettled) return;

    resolvedRef.current = true;
    setIsReady(true);

    if (useParam) {
      // U1 (plan 2026-09-25-003, Codex r3 #1) — the param decides WHICH view;
      // if the stored row is standing on that SAME id, its working state
      // comes back too (rung 2 folded into rung 1). This is sound because
      // every `applyView({ writeParam: true })` is followed by a persist of
      // `{ activeViewId: <same id>, workingState }` (debounced, flushed on
      // unmount) — a param this page wrote is always paired with a stored
      // row for that same id, so the stored working half is newer than the
      // view's clean definition by construction. A param naming a DIFFERENT
      // view than the stored row is unaffected (paramWorking stays null,
      // exactly as before). `skipWorkingRestore` mounts never restore,
      // param or not. See the precedence comment above for the full writeup.
      const paramWorking = !skipWorkingRestore && restored?.activeViewId === wanted
        ? (restored?.workingState ?? null)
        : null;
      if (isPresetParam) {
        applyView(presetById.get(wanted)!, 'preset', { workingOverride: paramWorking, writeParam: aliasedWanted !== null });
        return;
      }
      const saved = savedById.get(wanted);
      if (saved) {
        applyView(savedToFlowView(saved), 'saved', { workingOverride: paramWorking });
        return;
      }
      // Deleted or unknown id — degrade to the default view and say so.
      toast.error('That view no longer exists — showing the default view instead.');
      applyView(defaultPreset, 'preset');
      setViewParam(null);
      return;
    }

    // Rungs 2–4 below are the "this table's own state" path. Reaching them
    // with a FOREIGN param in the url means the rep just switched tabs, so the
    // url is currently describing the OTHER table: rewrite it to whatever this
    // one lands on. That is the only param write here — the foreign value is
    // never blanked, it is replaced by the correct one, so a copy of the url
    // always describes what is actually on screen (F14).
    const rewriteParam = isForeign;

    // 2. Stored active view + working state. A stored id that no longer
    //    resolves (someone deleted the view) drops silently to the next
    //    rung — the rep didn't click anything, so there is nothing to
    //    apologise for, and the string-pointer design makes the dangling
    //    reference harmless. Its working state is dropped with it: that
    //    state belonged to the view that's gone, and re-hanging it on the
    //    default view would show a phantom "Modified" banner.
    //
    //    SKIPPED ENTIRELY when `skipWorkingRestore` is set (`storedActiveId`
    //    is forced null above) — U2 / R1(b), 2026-08-27. WHY: every account
    //    drill-down shares ONE `contact_account` state row (that sharing is
    //    deliberate and stays — a per-account row would multiply
    //    unboundedly; see `useViewState.ts:46-59`), so restoring its working
    //    half replays account A's ad-hoc filters on account B. Reps (Annie
    //    Hale) read that as filters permanently "carrying over" into every
    //    drill-down. Spencer's decision: keep the shared row, keep WRITING
    //    to it, keep reading its `defaultViewId` pointer at rung 3 — just
    //    never restore its working state. The accepted tradeoff is that a
    //    filter a rep deliberately built in a drill-down does not survive a
    //    remount.
    const storedView = storedActiveId === null
      ? null
      : (presetById.get(storedActiveId)
        ?? (savedById.has(storedActiveId) ? savedToFlowView(savedById.get(storedActiveId)!) : null));
    if (storedView !== null) {
      applyView(
        storedView,
        presetById.has(storedView.id) ? 'preset' : 'saved',
        { workingOverride: restored?.workingState ?? null, writeParam: rewriteParam },
      );
      return;
    }

    // 3. The rep's chosen landing view (pointer only — no working state).
    const defaultView = storedDefaultId === null
      ? null
      : (presetById.get(storedDefaultId)
        ?? (savedById.has(storedDefaultId) ? savedToFlowView(savedById.get(storedDefaultId)!) : null));
    if (defaultView !== null) {
      applyView(defaultView, presetById.has(defaultView.id) ? 'preset' : 'saved', { writeParam: rewriteParam });
      return;
    }

    // 4. The code preset flagged default. A plain visit writes no `?view=` at
    //    all and keeps a clean URL; only the tab-switch case (above) rewrites
    //    the other table's leftover param to this one.
    applyView(defaultPreset, 'preset', { writeParam: rewriteParam });
  }, [ready, viewsSettled, viewParam, presetById, savedById, defaultPreset, belongsToOtherEntity, applyView, setViewParam, restored, skipWorkingRestore, presetIdAliases]);

  // ── Derived active-view identity ─────────────────────────────────────────
  const activePreset = presetById.get(activeViewId) ?? null;
  const activeSaved = savedById.get(activeViewId) ?? null;
  // The just-applied row, used ONLY when the list can't resolve the id (see
  // `appliedView`). The query stays authoritative the moment it can.
  const pendingView = appliedView !== null && appliedView.id === activeViewId ? appliedView : null;
  const activeView: FlowView =
    activePreset ?? (activeSaved ? savedToFlowView(activeSaved) : (pendingView ?? defaultPreset));
  const isPreset = activePreset !== null;
  // Ownership reads off the RESOLVED view rather than the list row, so a view
  // you just created is yours to Save immediately instead of after a refetch.
  const isOwnSaved = activePreset === null
    && myUserId !== null
    && activeView.createdBy === myUserId;
  // Save-in-place is exactly "your own saved view" now that `+ New view` is
  // name-first (U2a): there is no unsaved draft left to Save into existence.
  const canSave = isOwnSaved;
  const creatorName = activeSaved !== null && !isOwnSaved ? (activeSaved.creatorName ?? null) : null;
  const ownershipLabel = isPreset ? 'Global' : isOwnSaved ? 'You' : ownerDisplayLabel(activeSaved?.creatorName);

  const myViews = useMemo(
    () => (myUserId === null ? [] : views.filter(v => v.createdBy === myUserId)),
    [views, myUserId],
  );
  const teamViews = useMemo(
    () => views
      .filter(v => myUserId === null || v.createdBy !== myUserId)
      .sort((a, b) => (a.creatorName ?? '').localeCompare(b.creatorName ?? '') || a.name.localeCompare(b.name)),
    [views, myUserId],
  );

  const isDirty = !viewDefinitionsEqual(working, baseline);

  // ── Working-state setters ────────────────────────────────────────────────
  const setFilters = useCallback((filter: FilterGroup[]) => setWorking(w => ({ ...w, filter })), []);
  const setGroupLogic = useCallback((groupLogic: GroupLogic) => setWorking(w => ({ ...w, groupLogic })), []);
  const setSort = useCallback((sort: FlowSort) => setWorking(w => ({ ...w, sort })), []);
  const setGroupBy = useCallback((groupBy: string[]) => setWorking(w => ({ ...w, groupBy })), []);
  const setColumns = useCallback((columns: string[]) => setWorking(w => ({ ...w, columns })), []);
  const setColumnWidths = useCallback(
    (columnWidths: Record<string, number>) => setWorking(w => ({ ...w, columnWidths })),
    [],
  );
  // Reset restores the view's DEFINITION. Widths aren't in it (they're not in
  // `ViewDefinitionInput`, they're not in `definitionOf`, they don't dirty),
  // so there is nothing here for Reset to undo — carrying them across keeps
  // "Reset" meaning exactly one thing: discard my predicate/column edits.
  const reset = useCallback(
    () => setWorking(w => (w.columnWidths === undefined ? baseline : { ...baseline, columnWidths: w.columnWidths })),
    [baseline],
  );

  // The one way onto a just-created view (see the interface note). Shared by
  // `saveAsNew` and `BrowseAllModal`, so both land identically: definition
  // seeded from the SERVER's normalized row, `?view=` written, usage pinged.
  const applyCreated = useCallback((created: SavedViewWire) => {
    applyView(savedToFlowView(created), 'saved', { writeParam: true });
  }, [applyView]);

  // ── Save operations ──────────────────────────────────────────────────────
  // One private creator behind both public save-as-new entry points (S2):
  // `saveAsNew`/`saveCopyOf` differ only in which definition they POST.
  // `flowLayout` is the new row's layout pin (U11, KTD-6); omitted when absent.
  const createNamed = useCallback(async (
    rawName: string,
    definition: ReturnType<typeof definitionOf>,
    flowLayout?: FlowListLayoutId,
  ): Promise<SavedViewWire | null> => {
    // The caller's dialog already trims; trimming again keeps "a view is never
    // created blank" a property of the machine rather than of the UI.
    const name = rawName.trim();
    if (!name) return null;
    try {
      const created = await createView({ entity, name, ...definition, ...(flowLayout ? { flowLayout } : {}) });
      applyCreated(created);
      toast.success('Saved — copy its link to share it');
      return created;
    } catch (err) {
      toastUnlessRefused(err, 'Failed to save view');
      return null;
    }
  }, [createView, entity, applyCreated]);

  // The new view inherits the active view's pin unless the caller passes one.
  const saveAsNew = useCallback((rawName: string, pin?: { flowLayout?: FlowListLayoutId }): Promise<SavedViewWire | null> => (
    createNamed(rawName, definitionOf(working), pin?.flowLayout ?? activeView.flowLayout)
  ), [createNamed, working, activeView.flowLayout]);

  // adv 1 (plan 2026-09-28-001 U1): the pane row's "Save as new view…" must
  // copy THAT row's own saved definition, never the active working state —
  // reuses `workingFromView` + `definitionOf` so synthetic columns are
  // stripped exactly the way `saveAsNew` strips them. The copy carries the
  // source view's own pin (U11, KTD-6).
  const saveCopyOf = useCallback((view: FlowView, rawName: string): Promise<SavedViewWire | null> => (
    createNamed(rawName, definitionOf(workingFromView(view)), view.flowLayout)
  ), [createNamed]);

  const save = useCallback(async (pin?: { flowLayout?: FlowListLayoutId }): Promise<SavedViewWire | null> => {
    // Presets and other people's views are read-only — the button is
    // disabled, and this guard makes that a contract rather than a UI detail.
    if (!isOwnSaved) return null;
    const flowLayout = pin?.flowLayout;
    try {
      const updated = await updateView(activeViewId, { ...definitionOf(working), ...(flowLayout ? { flowLayout } : {}) });
      // Re-seed from the SERVER's normalized definition so a save followed by
      // a re-read can never read back as modified.
      applyView(savedToFlowView(updated), 'saved', { ping: false });
      toast.success('Saved');
      return updated;
    } catch (err) {
      toastUnlessRefused(err, 'Failed to save view');
      return null;
    }
  }, [isOwnSaved, activeViewId, updateView, working, applyView]);

  // ── Open ─────────────────────────────────────────────────────────────────
  // Applies the target view immediately — nothing to confirm first, see the
  // doc on `FlowViewMachine.openView` above for why (F8 + filter carry on
  // preset targets together made the old leave-guard dialog pointless).
  const openView = useCallback(async (id: string, opts?: { carry?: boolean }): Promise<boolean> => {
    // View-switch filter carry (product ask, 2026-08-12) — the view being
    // LEFT's committed filter, carried onto the newly active view's working
    // state IF the target is a preset. A saved target ignores this entirely
    // (Q2 fix, 2026-08-13 — see the saved branch below and `applyView`'s
    // `carryFilterFrom` doc for the full mechanism).
    //
    // GTMOPS-2253: only carry when the outgoing view is ACTUALLY dirty. When
    // it's clean, `working` IS that view's own stored predicate (equal to
    // `baseline`), so carrying it unconditionally onto the target would
    // splice the outgoing view's own filter onto a target whose baseline
    // comes from ITS OWN definition — the dirty diff then reads true even
    // though the rep never edited anything. Gating on `viewDefinitionsEqual`
    // makes "nothing to carry" the null case instead of "always carry
    // whatever's on screen."
    //
    // U2 / R1(c) (2026-08-27): `disableFilterCarry` opts a mount out of the
    // carry entirely — a deliberate PARTIAL reversal of the 2026-08-12 ask,
    // account mounts only (see the option's doc comment). Forced to null
    // HERE, at the single computation site, so BOTH consumers below (the
    // preset branch and the unresolvable-id fallback) are covered by
    // construction rather than by two call sites remembering.
    const carryFilterFrom = disableFilterCarry || opts?.carry === false || viewDefinitionsEqual(working, baseline)
      ? null
      : { filter: working.filter, groupLogic: working.groupLogic };

    const preset = presetById.get(id);
    if (preset) {
      applyView(preset, 'preset', { writeParam: true, carryFilterFrom: carryFilterFrom ?? undefined });
      return true;
    }
    const saved = savedById.get(id);
    if (saved) {
      // A saved target is authoritative — it owns its complete predicate, so
      // no carry (Q2 fix, docs/flow/shared-view-vs-role-defaults-bug.md).
      // This makes the switcher path byte-identical to the `?view=` deep
      // link (`:508-523` — the `useParam` saved branch, which never carries).
      applyView(savedToFlowView(saved), 'saved', { writeParam: true });
      return true;
    }
    toast.error('That view no longer exists — showing the default view instead.');
    applyView(defaultPreset, 'preset', { carryFilterFrom: carryFilterFrom ?? undefined });
    setViewParam(null);
    return false;
  }, [presetById, savedById, defaultPreset, applyView, setViewParam, working, baseline, disableFilterCarry]);

  const renameView = useCallback(async (view: SavedViewWire, rawName: string): Promise<void> => {
    // The name comes from the caller's UI — `ViewNameDialog` today, an inline
    // input in the switcher next (U2b). Either way an unchanged or blank name
    // is a no-op rather than a pointless PUT.
    const name = rawName.trim();
    if (!name || name === view.name) return;
    try {
      // Name is NOT part of the dirty definition — this deliberately touches
      // no working state, so a rename never trips the Modified banner.
      await updateView(view.id, { name });
      toast.success('Renamed');
    } catch (err) {
      toastUnlessRefused(err, 'Failed to rename view');
    }
  }, [updateView]);

  const deleteView = useCallback(async (view: SavedViewWire): Promise<void> => {
    try {
      await deleteViewRow(view.id);
      toast.success('View deleted');
      // Standing on the deleted view: drop to the entity's default preset.
      if (view.id === activeViewId) applyView(defaultPreset, 'preset', { writeParam: true });
    } catch (err) {
      toastUnlessRefused(err, 'Failed to delete view');
    }
  }, [deleteViewRow, activeViewId, applyView, defaultPreset]);

  // "Set as default" (U9) — writes ONLY the `defaultViewId` pointer, so it
  // never disturbs the rep's live working state. Accepts preset slugs and
  // saved-view ids alike (the column is a plain string, not an FK).
  const setDefault = viewState?.setDefaultView ?? null;
  const setDefaultView = useMemo(
    () => (setDefault === null ? null : (id: string) => {
      void setDefault(id).then(() => toast.success('Default view updated'));
    }),
    [setDefault],
  );

  const copyLink = useCallback(async (id: string, requestName?: SuggestViewName): Promise<void> => {
    let targetId = id;
    // A link points at the SAVED view — copying the active view while it has
    // unsaved edits pushes through Save-as-new first (doc: R1). This does NOT
    // apply to a preset: a preset's link (`?view=<slug>`) always re-derives
    // the canonical preset fresh on open (role defaults + its own predicate),
    // so it never "shows something other than what's on screen" the way a
    // stale saved-view link would — there is no owned, mutable "your saved
    // version" for a preset to diverge from. Without this guard, the
    // filter-carry feature (which intentionally makes `working` differ from
    // `baseline` on a plain preset-to-preset switch — see
    // docs/flow/shared-view-vs-role-defaults-bug.md) made Copy Link on a
    // freshly-opened, unedited preset spuriously pop the Save-as-new dialog
    // (Spencer report 2026-08-13).
    if (id === activeViewId && isDirty && !isPreset) {
      if (requestName === undefined) {
        // No naming UI available (a caller that didn't wire one up). Handing
        // over a link to the SAVED definition would quietly show the reader
        // something other than what's on screen, so refuse instead.
        toast.error('Save this view first — a link always points at the saved version.');
        return;
      }
      const name = await requestName(`${activeView.name} (my copy)`);
      if (name === null) return;
      const created = await saveAsNew(name);
      if (!created) return;
      targetId = created.id;
    }
    // A section mount's link must carry the SECTION's own key
    // (`urlParamKey`), not a hardcoded `'view'` — otherwise the recipient's
    // machine (namespaced to its own `?view_<key>=`) never sees the param
    // and the copied link silently opens on the recipient's own default
    // instead of the section the link was copied from (plan 2026-09-25-003
    // U1, Codex r3 #2).
    const url = new URL(window.location.href);
    url.searchParams.set(urlParamKey, targetId);
    await copyViewLink(url.toString());
  }, [activeViewId, activeView, isDirty, isPreset, saveAsNew, urlParamKey]);

  return {
    entity,
    isReady,
    presets,
    myViews,
    teamViews,
    isLoadingViews: viewsQuery.isLoading,
    activeViewId,
    activeView,
    isPreset,
    canSave,
    creatorName,
    ownershipLabel,
    working,
    baseline,
    isDirty,
    isSaving: isMutating,
    setFilters,
    setGroupLogic,
    setSort,
    setGroupBy,
    setColumns,
    setColumnWidths,
    openView,
    applyCreated,
    reset,
    save,
    saveAsNew,
    saveCopyOf,
    renameView,
    deleteView,
    copyLink,
    defaultViewId: viewState?.defaultViewId ?? null,
    setDefaultView,
    refetchViews,
  };
}
